import React, { useState } from 'react';

export default function CloudSolutions() {
  const [formData, setFormData] = useState({ name: '', email: '', cloudProvider: 'AWS', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 font-sans selection:bg-orange-500 selection:text-white">
      
     

      {/* ==========================================
          SECTION 1: HERO / CLOUD INFRASTRUCTURE
         ========================================== */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-28 border-b border-stone-200">
        {/* Subtle Background Elements */}
        <div className="absolute top-1/3 right-1/4 -z-10 w-96 h-96 bg-orange-400/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute -top-20 -left-20 -z-10 w-96 h-96 bg-amber-300/20 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px]" />

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Text Column */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 font-semibold text-xs tracking-wider uppercase">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-ping" />
              Next-Gen Cloud Architecture
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-stone-900 tracking-tight leading-[1.1]">
              Cloud Infrastructure <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-amber-500 to-orange-500">
                Engineered for Scale
              </span>
            </h1>

            <p className="text-lg text-stone-600 max-w-xl leading-relaxed">
              High-availability, ultra-secure cloud environments built to accelerate growth, drive performance, and cut infrastructure overhead.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="#consultation-section"
                className="px-8 py-4 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-bold transition-all duration-200 shadow-xl shadow-orange-500/25 hover:shadow-orange-500/40 transform hover:-translate-y-0.5"
              >
                Migrate to Cloud
              </a>
              <a
                href="#architecture-section"
                className="px-8 py-4 rounded-xl bg-stone-100 hover:bg-stone-200 border border-stone-200 text-stone-800 font-semibold transition-all duration-200"
              >
                Explore Services
              </a>
            </div>

            {/* Metrics */}
            <div id="metrics-section" className="grid grid-cols-3 gap-6 pt-8 border-t border-stone-200">
              <div>
                <p className="text-3xl font-extrabold text-stone-900">99.99%</p>
                <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mt-1">Uptime SLA</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-orange-600">30%+</p>
                <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mt-1">Cost Savings</p>
              </div>
              <div>
                <p className="text-3xl font-extrabold text-stone-900">&lt; 10ms</p>
                <p className="text-xs font-medium text-stone-500 uppercase tracking-wider mt-1">Latency Goal</p>
              </div>
            </div>
          </div>

          {/* Right Visual Dashboard Card */}
          <div className="lg:col-span-5 relative">
            <div className="relative bg-stone-900 text-white border border-stone-800 rounded-3xl p-6 shadow-2xl">
              <div className="flex items-center justify-between pb-4 border-b border-stone-800">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-xs font-mono text-stone-300">Global Cluster: Active</span>
                </div>
                <span className="text-[11px] font-mono bg-orange-500/10 text-orange-400 border border-orange-500/30 px-2.5 py-0.5 rounded-full">
                  Multi-Region
                </span>
              </div>

              {/* Simulated Cloud Telemetry */}
              <div className="py-6 space-y-5">
                <div>
                  <div className="flex justify-between text-xs font-mono text-stone-400 mb-2">
                    <span>Kubernetes CPU Load</span>
                    <span className="text-orange-400 font-bold">34%</span>
                  </div>
                  <div className="w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[34%]" />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-mono text-stone-400 mb-2">
                    <span>Auto-Scaling Nodes</span>
                    <span className="text-orange-400 font-bold">18 / 64 Active</span>
                  </div>
                  <div className="w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[60%]" />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-mono text-stone-400 mb-2">
                    <span>Data Throughput</span>
                    <span className="text-orange-400 font-bold">1.2 GB/s</span>
                  </div>
                  <div className="w-full h-2.5 bg-stone-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 w-[82%]" />
                  </div>
                </div>
              </div>

              <div className="p-3.5 bg-stone-950 rounded-xl border border-stone-800 flex items-center justify-between text-xs font-mono">
                <span className="text-stone-400">Security Standard</span>
                <span className="text-emerald-400 font-semibold flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  AES-256 Compliant
                </span>
              </div>
            </div>
          </div>

        </div>
      </section>


      {/* ==========================================
          SECTION 2: CLOUD CAPABILITIES & SERVICES
         ========================================== */}
      <section id="architecture-section" className="py-24 bg-stone-100">
        <div className="max-w-7xl mx-auto px-6">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <span className="text-orange-600 font-bold text-xs uppercase tracking-widest bg-orange-100 border border-orange-200 px-3 py-1 rounded-full">
              Our Expertise
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              End-to-End Cloud Services
            </h2>
            <p className="text-stone-600 text-base">
              Architecting resilient, cost-efficient, and future-proof cloud solutions tailored to your operational demands.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Service 1 */}
            <div className="bg-white p-8 rounded-2xl border border-stone-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Cloud Migration
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Smoothly transition complex enterprise applications to AWS, Azure, or GCP with zero downtime and total integrity.
              </p>
            </div>

            {/* Service 2 */}
            <div className="bg-white p-8 rounded-2xl border border-stone-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                DevOps & IaC Automation
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Streamline release cycles using automated Terraform infrastructure provisioning and modern CI/CD pipelines.
              </p>
            </div>

            {/* Service 3 */}
            <div className="bg-white p-8 rounded-2xl border border-stone-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Serverless Architecture
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Deploy event-driven container workloads with Kubernetes and serverless backends designed for extreme efficiency.
              </p>
            </div>

            {/* Service 4 */}
            <div className="bg-white p-8 rounded-2xl border border-stone-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Security & Compliance
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Fortify data safety with zero-trust network policies, SOC2 compliance roadmaps, and continuous vulnerability scans.
              </p>
            </div>

            {/* Service 5 */}
            <div className="bg-white p-8 rounded-2xl border border-stone-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                Cost & FinOps Optimization
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Cut cloud spend dramatically with active node right-sizing, reserved instances, and precise cost-allocation models.
              </p>
            </div>

            {/* Service 6 */}
            <div className="bg-white p-8 rounded-2xl border border-stone-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-600 group-hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                24/7 Managed Operations
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Proactive cluster monitoring, automated failover setup, and around-the-clock incident response protocols.
              </p>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SECTION 3: CLOUD ROADMAP & CONSULTATION
         ========================================== */}
      <section id="consultation-section" className="py-24 bg-white border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-stone-900 text-white rounded-3xl p-8 lg:p-14 shadow-2xl relative overflow-hidden">
            
            {/* Gradient Accents */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-orange-500/10 blur-3xl pointer-events-none" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
              
              {/* Left Column: Migration Process */}
              <div className="lg:col-span-6 space-y-6">
                <span className="bg-orange-500/10 text-orange-400 border border-orange-500/30 font-semibold text-xs px-3.5 py-1.5 rounded-full uppercase tracking-wider">
                  Transformation Process
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                  Your Clear Path to Modern Cloud Infrastructure
                </h2>
                <p className="text-stone-400 text-base leading-relaxed">
                  Our certified architects will assess your setup and build a frictionless plan to scale performance and lower operating costs.
                </p>

                {/* Steps */}
                <div className="space-y-6 pt-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white font-bold flex items-center justify-center shrink-0 shadow-md shadow-orange-600/30">
                      01
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Comprehensive Audit</h4>
                      <p className="text-sm text-stone-400 mt-0.5">Analyze current performance bottlenecks, security risks, and cost efficiency.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white font-bold flex items-center justify-center shrink-0 shadow-md shadow-orange-600/30">
                      02
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Custom Architecture Blueprint</h4>
                      <p className="text-sm text-stone-400 mt-0.5">Design a resilient hybrid or multi-cloud ecosystem customized to your stack.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white font-bold flex items-center justify-center shrink-0 shadow-md shadow-orange-600/30">
                      03
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-base">Seamless Automated Execution</h4>
                      <p className="text-sm text-stone-400 mt-0.5">Deploy workloads with zero downtime and full automated continuous integration.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Interactive Form */}
              <div className="lg:col-span-6">
                <div className="bg-white text-stone-900 border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-2xl">
                  <h3 className="text-2xl font-bold text-stone-900 mb-1">
                    Book a Free Cloud Assessment
                  </h3>
                  <p className="text-stone-600 text-sm mb-6">
                    Speak directly with a senior cloud engineer — no sales pitch.
                  </p>

                  {submitted ? (
                    <div className="bg-orange-50 border border-orange-200 text-stone-800 p-8 rounded-xl text-center space-y-3">
                      <div className="w-14 h-14 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto text-2xl font-bold shadow-lg shadow-orange-600/30">
                        ✓
                      </div>
                      <h4 className="font-bold text-xl text-stone-900">Request Received!</h4>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        An architect will review your submission and connect within 24 hours to schedule your strategy session.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Full Name
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="Alex Morgan"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Work Email
                        </label>
                        <input
                          type="email"
                          required
                          placeholder="alex@company.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Primary Cloud Target
                        </label>
                        <select
                          value={formData.cloudProvider}
                          onChange={(e) => setFormData({ ...formData, cloudProvider: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        >
                          <option value="AWS">Amazon Web Services (AWS)</option>
                          <option value="Azure">Microsoft Azure</option>
                          <option value="GCP">Google Cloud Platform (GCP)</option>
                          <option value="Hybrid">Hybrid / Multi-Cloud</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-stone-700 uppercase tracking-wider mb-1.5">
                          Infrastructure Goals
                        </label>
                        <textarea
                          rows="3"
                          required
                          placeholder="Briefly describe your current setup or migration targets..."
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl bg-stone-50 border border-stone-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-stone-900 text-sm transition-all"
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-4 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-orange-600/30 hover:shadow-orange-600/40"
                      >
                        Request Cloud Audit
                      </button>
                    </form>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>


    </div>
  );
}